import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { findFund } from "../../component/Methods";

const initialState = {
  funds: [],
  fundsWithLotsAndStrategies: [],
  loading: false,
  error: null,
};

const fundsSlice = createSlice({
  name: "funds",
  initialState,
  reducers: {
    // Set funds data (from WebSocket)
    setFunds: (state, action) => {
      state.funds = action.payload || [];
      state.loading = false;
      state.error = null;
    },

    // Set funds with lots and strategies data (from WebSocket)
    setFundsWithLotsAndStrategies: (state, action) => {
      state.fundsWithLotsAndStrategies = action.payload || [];
      state.loading = false;
      state.error = null;
    },

    // Add new fund (from WebSocket response)
    addFund: (state, action) => {
      const fundData = action.payload;
      if (fundData?.fund) {
        const fund = fundData.fund;
        // Check if fund already exists
        const existingIndex = state.funds.findIndex(
          (f) => f.fundId === fund.fundId
        );
        if (existingIndex === -1) {
          state.funds.push(fund);
          toast.success(`${fund.name} has been successfully added.`);
        } else {
          // Update existing fund
          state.funds[existingIndex] = fund;
          toast.success(`${fund.name} has been updated.`);
        }
      } else {
        toast.error("Failed to add fund. Please try again.");
      }
      state.loading = false;
      state.error = null;
    },

    // Update existing fund (from WebSocket response)
    updateFund: (state, action) => {
      const fund = action.payload;
      const index = state.funds.findIndex((f) => f.fundId === fund.fundId);
      if (index !== -1) {
        state.funds[index] = fund;
        toast.success(`${fund.name} has been updated successfully`);
      } else {
        // If fund doesn't exist, add it
        state.funds.push(fund);
        toast.success(`${fund.name} has been added successfully`);
      }
      state.loading = false;
      state.error = null;
    },

    // Delete fund (from WebSocket response)
    deleteFund: (state, action) => {
      const fundId = action.payload?.fundId;
      const fundIndex = state.funds.findIndex((f) => f.fundId === fundId);
      if (fundIndex !== -1) {
        const fundName = state.funds[fundIndex].name;
        state.funds.splice(fundIndex, 1);
        toast.success(`${fundName} has been deleted successfully`);
      }
      state.loading = false;
      state.error = null;
    },
    // Add SMA (from WebSocket response)
    addSMA: (state, action) => {
      const responseData = action.payload;
      
      if (responseData?.fund) {
        const smaData = responseData.fund;
        const { smaId, account, ...rest } = smaData;
        
        // Transform SMA structure to fund structure (same as App.js pattern)
        const accounts = {
          [account]: {
            number: account,
            custodian: "2400a58dc924508a59bf4ade3e4867f7", // Default custodian
          },
        };
        
        const fund = { ...rest, fundId: smaId, accounts,isSMA: true };        
        // Check if fund already exists
        const existingIndex = state.funds.findIndex(
          (f) => f.fundId === fund.fundId
        );
        if (existingIndex === -1) {
          state.funds.push(fund);
          toast.success(`${fund.name || fund.abbreviation} SMA has been successfully added.`);
        } else {
          // Update existing fund
          state.funds[existingIndex] = fund;
          toast.success(`${fund.name || fund.abbreviation} SMA has been updated.`);
        }
      } else {
        toast.error("Failed to add SMA. Missing required fields (smaId, account).");
      }
      state.loading = false;
      state.error = null;
    },

    // Update SMA (from WebSocket response)
    updateSMA: (state, action) => {
      const responseData = action.payload;
      
      if (responseData?.sma) {
        const smaData = responseData.sma;
        const { smaId, account, ...rest } = smaData;
        
        // Transform SMA structure to fund structure
        const accounts = {
          [account]: {
            number: account,
            custodian: "2400a58dc924508a59bf4ade3e4867f7", // Default custodian
          },
        };
        
        const fund = { ...rest, fundId: smaId, accounts, isSMA: true };
        
        // Find and update existing SMA
        const existingIndex = state.funds.findIndex(
          (f) => f.fundId === smaId
        );
        
        if (existingIndex !== -1) {
          // Preserve existing properties that are not in the update
          state.funds[existingIndex] = {
            ...state.funds[existingIndex],
            ...fund,
          };
          toast.success(`${fund.name || fund.abbreviation} SMA has been updated successfully.`);
        } else {
          toast.error("SMA not found.");
        }
      } else {
        toast.error("Failed to update SMA. Invalid response data.");
      }
      state.loading = false;
      state.error = null;
    },

    // Update fund allocation (from WebSocket response)

    updateFundAllocation: (state, action) => {
      const { fundId, items, smaId } = action.payload;

      if (state.funds && Array.isArray(state.funds) && state.funds.length > 0) {
        const funds = state.funds;
        if (Array.isArray(funds)) {
          const fundIndex = funds.findIndex(
            (fund) =>
              (fundId && fund.fundId === fundId) ||
              (smaId && fund.fundId === smaId)
          );
          if (fundIndex !== -1) {
            funds[fundIndex] = {
              ...funds[fundIndex],
              allocation: { items: items },
            };
            toast.success(
              `Allocation for ${findFund(
                funds[fundIndex].fundId,
                state.funds
              )} is Updated`
            );
          } else {
            toast.error(
              `${findFund(fundId || smaId, state.funds)} not found in state.`
            );
          }
        } else {
          toast.error("funds array is undefined in state.");
        }
      }
      state.loading = false;
      state.error = null;
    },

    // Blacklist functions - regular reducers like other functions
    blacklistRulesFunds: (state, action) => {
      const { fund, sma, blacklist } = action.payload;
      const id = fund?.fundId || sma?.smaId;
      const fundIndex = state.funds.findIndex((f) => f.fundId === id);

      if (fundIndex !== -1) {
        const updatedBlacklist = sma
          ? { ...blacklist, fundId: blacklist.smaId, smaId: undefined }
          : blacklist;

        state.funds[fundIndex] = {
          ...state.funds[fundIndex],
          blacklist: updatedBlacklist,
        };
      }
    },

    blacklistRulesModifiedFunds: (state, action) => {
      const { blacklist } = action.payload;
      const id = blacklist?.fundId || blacklist?.smaId;
      const fundIndex = state.funds.findIndex((f) => f.fundId === id);

      if (fundIndex !== -1) {
        state.funds[fundIndex].blacklist = {
          ...state.funds[fundIndex].blacklist,
          rules: blacklist.rules,
        };
      }
    },

    blacklistDeleteFunds: (state, action) => {
      const { blacklist } = action.payload;
      const id = blacklist?.fundId || blacklist?.smaId;
      const fundIndex = state.funds.findIndex((f) => f.fundId === id);
      if (fundIndex !== -1) {
        state.funds[fundIndex].blacklist = {
          ...state.funds[fundIndex].blacklist,
          rules: [],
        };
      }
    },
    // Set loading state
    setFundsLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error state
    setFundsError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear error state
    clearFundsError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setFunds,
  setFundsWithLotsAndStrategies,
  addFund,
  addSMA,
  updateSMA,
  updateFund,
  deleteFund,
  updateFundAllocation,
  setFundsLoading,
  setFundsError,
  clearFundsError,
  blacklistRulesFunds,
  blacklistRulesModifiedFunds,
  blacklistDeleteFunds,
} = fundsSlice.actions;

// Selectors - Direct access to funds state
export const selectFunds = (state) => state.funds?.funds || [];
export const selectFundsWithLotsAndStrategies = (state) =>
  state.funds?.fundsWithLotsAndStrategies || [];
export const selectFundsLoading = (state) => state.funds?.loading || false;
export const selectFundsError = (state) => state.funds?.error || null;

export default fundsSlice.reducer;
